import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create a base axios instance for product operations
const adminProductApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin`,
});

// Request interceptor to add token
adminProductApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminProductApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminProductService = {
  // ==================== PRODUCTS ====================
  /**
   * Get all products with filters and pagination
   */
  getProducts: async (params = {}) => {
    console.log('📤 [ProductService] Fetching products...', params);
    try {
      const response = await adminProductApi.get('/products', { params });
      console.log('📥 [ProductService] Products fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error fetching products:', error);
      throw error;
    }
  },

  /**
   * Get product by ID
   */
  getProductById: async (productId) => {
    console.log('📤 [ProductService] Fetching product:', productId);
    try {
      const response = await adminProductApi.get(`/products/${productId}`);
      console.log('📥 [ProductService] Product fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error fetching product:', error);
      throw error;
    }
  },

  /**
   * Create new product
   */
  createProduct: async (productData) => {
    console.log('📤 [ProductService] Creating product:', productData);
    try {
      const response = await adminProductApi.post('/products', productData);
      console.log('📥 [ProductService] Product created:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error creating product:', error);
      throw error;
    }
  },

  /**
   * Update product
   */
  updateProduct: async (productId, productData) => {
    console.log('📤 [ProductService] Updating product:', productId);
    try {
      const response = await adminProductApi.put(`/products/${productId}`, productData);
      console.log('📥 [ProductService] Product updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error updating product:', error);
      throw error;
    }
  },

  /**
   * Update product status
   */
  updateProductStatus: async (productId, status) => {
    console.log('📤 [ProductService] Updating product status:', productId, status);
    try {
      const response = await adminProductApi.patch(`/products/${productId}/status`, { status });
      console.log('📥 [ProductService] Product status updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error updating product status:', error);
      throw error;
    }
  },

  /**
   * Delete product (soft delete - archive)
   */
  deleteProduct: async (productId) => {
    console.log('📤 [ProductService] Deleting product:', productId);
    try {
      const response = await adminProductApi.delete(`/products/${productId}`);
      console.log('📥 [ProductService] Product deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error deleting product:', error);
      throw error;
    }
  },

  /**
   * Get product statistics
   */
  getProductStats: async () => {
    console.log('📤 [ProductService] Fetching product statistics...');
    try {
      const response = await adminProductApi.get('/products/stats');
      console.log('📥 [ProductService] Product statistics fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error fetching product statistics:', error);
      throw error;
    }
  },

  /**
   * Search products
   */
  searchProducts: async (query, params = {}) => {
    console.log('📤 [ProductService] Searching products:', query);
    try {
      const response = await adminProductApi.get('/products/search', {
        params: { ...params, query }
      });
      console.log('📥 [ProductService] Products search results:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error searching products:', error);
      throw error;
    }
  },

  /**
   * Bulk update products
   */
  bulkUpdateProducts: async (productIds, data) => {
    console.log('📤 [ProductService] Bulk updating products:', productIds);
    try {
      const response = await adminProductApi.post('/products/bulk-update', {
        productIds,
        data
      });
      console.log('📥 [ProductService] Products bulk updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error bulk updating products:', error);
      throw error;
    }
  },

  /**
   * Export products
   */
  exportProducts: async (format = 'csv') => {
    console.log('📤 [ProductService] Exporting products...');
    try {
      const response = await adminProductApi.get('/products/export', {
        params: { format },
        responseType: 'blob'
      });
      console.log('📥 [ProductService] Products exported');
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error exporting products:', error);
      throw error;
    }
  },

  // ==================== PRODUCT-SPECIFIC COLLECTION METHODS ====================
  /**
   * Get collection-based products (for dropdown)
   */
  getCollectionBasedProducts: async (params = {}) => {
    console.log('📤 [ProductService] Fetching collection-based products...');
    try {
      const response = await adminProductApi.get('/products/collection-based', { params });
      console.log('📥 [ProductService] Collection-based products fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('💥 [ProductService] Error fetching collection-based products:', error);
      throw error;
    }
  },

  // ==================== UTILITIES ====================
  /**
   * Upload image to Cloudinary
   */
  uploadImageToCloudinary: async (file, uploadPreset = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset || process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30000
        }
      );
      
      return {
        url: response.data.secure_url,
        publicId: response.data.public_id,
        width: response.data.width,
        height: response.data.height,
        format: response.data.format
      };
    } catch (error) {
      console.error('💥 [ProductService] Error uploading to Cloudinary:', error);
      throw error;
    }
  },

  /**
   * Validate ObjectId
   */
  isValidObjectId: (id) => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  },

  /**
   * Format price
   */
  formatPrice: (price) => {
    return `₦${parseFloat(price || 0).toLocaleString()}`;
  },

  /**
   * Get status color
   */
  getStatusColor: (status) => {
    const colors = {
      active: 'green',
      draft: 'yellow',
      inactive: 'gray',
      archived: 'red'
    };
    return colors[status] || 'gray';
  },

  /**
   * Format date
   */
  formatDate: (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  /**
   * Generate slug from name
   */
  generateSlug: (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }
};

export default adminProductService;

